import { useState } from "react";
import { useApp }  from "../context/AppContext";
import { useCart } from "../context/CartContext";
import Breadcrumb  from "../components/Breadcrumb";

export default function ProductDetailPage() {
  const { activeProduct, navigate } = useApp();
  const { addToCart } = useCart();

  const [selectedFlavor, setSelectedFlavor] = useState("");
  const [selectedSize,   setSelectedSize]   = useState("");
  const [quantity,       setQuantity]       = useState(1);
  const [justAdded,      setJustAdded]      = useState(false);
  const [error,          setError]          = useState("");

  if (!activeProduct) {
    navigate("products", "other");
    return null;
  }

  const hasFlavors = activeProduct.flavors?.length > 0;
  const hasSizes   = activeProduct.sizes?.length > 0;
  const lineTotal  = (activeProduct.priceNum * quantity).toFixed(2);
  const useFlavorDropdown = activeProduct.flavors?.length > 10;

  const handleAdd = () => {
    if (hasFlavors && !selectedFlavor) {
      setError("Please choose a flavor before adding to cart.");
      return;
    }
    if (hasSizes && !selectedSize) {
      setError("Please choose a size before adding to cart.");
      return;
    }
    setError("");
    const variant = [selectedFlavor, selectedSize].filter(Boolean).join(" · ") || "Standard";
    addToCart(activeProduct, variant, quantity);
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 2500);
  };

  const crumbs = [
    { label: "Home",     page: "home" },
    { label: "Products", page: "products" },
    { label: "Other",    page: "products", cat: "other" },
    { label: activeProduct.name },
  ];

  return (
    <div className="page-wrap fade-up">
      <Breadcrumb crumbs={crumbs} />

      <div className="detail-layout">

        {/* ── Image panel ── */}
        <div className="detail-image-col">
          <div className="detail-image-box">
            <span className="detail-emoji">{activeProduct.emoji}</span>
            {activeProduct.badge && (
              <span className={`product-badge detail-badge ${activeProduct.badgeColor}`}>
                {activeProduct.badge}
              </span>
            )}
          </div>

          {/* Mobile: show price under image */}
          <div className="detail-price-mobile">
            {activeProduct.price} <span>per unit</span>
          </div>
        </div>

        {/* ── Info + options panel ── */}
        <div className="detail-info-col">
          <div className="detail-cat-tag">📦 Other</div>
          <h1 className="detail-title">{activeProduct.name}</h1>
          <div className="detail-price-desktop">
            {activeProduct.price} <span>per unit</span>
          </div>

          <p className="detail-long-desc">{activeProduct.details || activeProduct.desc}</p>

          <div className="detail-divider" />

          {/* Flavor selector */}
{hasFlavors && (
  <div className="detail-option-group">
    <div className="detail-option-label">
      Flavor <span className="detail-required">*</span>
    </div>

        {/* Dropdown if many flavors */}
        {useFlavorDropdown ? (
          <select
            className="detail-size-select"
            value={selectedFlavor}
            onChange={(e) => {
              setSelectedFlavor(e.target.value);
              setError("");
            }}
          >
            <option value="">— Choose a flavor —</option>
            {activeProduct.flavors.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        ) : (
          /* Pills if few flavors */
          <div className="detail-flavor-pills">
            {activeProduct.flavors.map((f) => (
              <button
                key={f}
                className={`detail-flavor-pill ${selectedFlavor === f ? "selected" : ""}`}
                onClick={() => {
                  setSelectedFlavor(f);
                  setError("");
                }}
              >
                {f}
              </button>
            ))}
          </div>
        )}
      </div>
    )}

          {/* Size dropdown */}
          {hasSizes && (
            <div className="detail-option-group">
              <div className="detail-option-label">
                Size <span className="detail-required">*</span>
              </div>
              <select
                className="detail-size-select"
                value={selectedSize}
                onChange={(e) => { setSelectedSize(e.target.value); setError(""); }}
              >
                <option value="">— Choose a size —</option>
                {activeProduct.sizes.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          )}

          {/* Quantity stepper */}
          <div className="detail-option-group">
            <div className="detail-option-label">Quantity</div>
            <div className="detail-qty-row">
              <button className="detail-qty-btn" onClick={() => setQuantity((q) => Math.max(1, q - 1))}>−</button>
              <span className="detail-qty-num">{quantity}</span>
              <button className="detail-qty-btn" onClick={() => setQuantity((q) => q + 1)}>+</button>
              <span className="detail-subtotal">Subtotal: <strong>${lineTotal}</strong></span>
            </div>
          </div>

          {/* Validation error */}
          {error && <p className="detail-error">⚠️ {error}</p>}

          {/* Add to cart */}
          <button
            className={`detail-add-btn ${justAdded ? "added" : ""}`}
            onClick={handleAdd}
          >
            {justAdded ? "✓ Added to Cart!" : "Add to Cart 🛒"}
          </button>

          {/* Quick link to cart after adding */}
          {justAdded && (
            <button className="detail-goto-cart" onClick={() => navigate("cart")}>
              View Cart →
            </button>
          )}

          <div className="detail-pickup-note">
            🏪 Available for <strong>in-store pickup only</strong> at London ON.
          </div>
        </div>
      </div>
    </div>
  );
}
